import type { ApiResponse } from '@cmoh/shared'
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { type AxiosRequestConfig, api } from '../lib/axios'

/**
 * Helper to unwrap ApiResponse<T> or throw with API message
 */
export function unwrapApi<T>(resp: ApiResponse<T>): T {
  if (resp.success) return resp.data
  throw new Error(resp.error?.message || 'Unknown API error')
}

/**
 * Build function to construct a request dynamically from mutation variables
 */
export type BuildRequest<TVars> = (vars: TVars) => {
  method: 'get' | 'post' | 'put' | 'patch' | 'delete'
  url: string
  data?: unknown
  config?: AxiosRequestConfig
}

/**
 * Generic GET with React Query
 * By default, returns TData (unwrapped) vía select; puedes anularlo con options.select
 */
export function useApiQuery<TData>(
  queryKey: readonly (string | number | boolean | null | undefined)[],
  url: string,
  config?: AxiosRequestConfig,
  options?: Omit<
    UseQueryOptions<ApiResponse<TData>, Error, TData>,
    'queryKey' | 'queryFn' | 'select'
  > & {
    select?: (resp: ApiResponse<TData>) => TData
  },
) {
  return useQuery<ApiResponse<TData>, Error, TData>({
    queryKey,
    queryFn: () => api.get<TData>(url, config),
    // por defecto desempaquetamos; si pasas options.select, lo respeta
    select: options?.select ?? ((resp) => unwrapApi(resp)),
    ...options,
  })
}

/**
 * Ultra-flexible mutation based on a build function
 * Soporta cualquier método, URL dinámica, body y config por variables.
 */
export function useApiRequestMutation<TData, TVars = unknown>(
  build: BuildRequest<TVars>,
  options?: Omit<UseMutationOptions<ApiResponse<TData>, Error, TVars>, 'mutationFn'>,
) {
  return useMutation<ApiResponse<TData>, Error, TVars>({
    mutationFn: async (vars: TVars) => {
      const { method, url, data, config } = build(vars)
      switch (method) {
        case 'post':
          return api.post<TData>(url, data, config)
        case 'put':
          return api.put<TData>(url, data, config)
        case 'patch':
          return api.patch<TData>(url, data, config)
        case 'delete':
          return api.delete<TData>(url, config) // si necesitás body en DELETE: ponlo en config.data
        case 'get':
          return api.get<TData>(url, config)
        default:
          // Type-safe guard si amplías métodos
          throw new Error(`Unsupported HTTP method: ${method satisfies never}`)
      }
    },
    ...options,
  })
}

/**
 * Invalidation helpers
 */
export function useInvalidateQueries() {
  const queryClient = useQueryClient()
  const invalidateQueries = (queryKey: readonly (string | number | boolean | null | undefined)[]) =>
    queryClient.invalidateQueries({ queryKey })
  const invalidateAllQueries = () => queryClient.invalidateQueries()
  return { invalidateQueries, invalidateAllQueries }
}

/**
 * Prefetch helper (GET)
 */
export function usePrefetchQuery() {
  const queryClient = useQueryClient()
  const prefetchQuery = <TData>(
    queryKey: readonly (string | number | boolean | null | undefined)[],
    url: string,
    config?: AxiosRequestConfig,
  ) =>
    queryClient.prefetchQuery({
      queryKey,
      queryFn: () => api.get<TData>(url, config),
      staleTime: 5 * 60 * 1000,
    })
  return { prefetchQuery }
}
